import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_faith-bubbles-io/artifacts/j7evy55i_WINNERS%20LOGO%20REAL.png";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Events", href: "#events" },
  { label: "Testimonies", href: "#testimonies" },
  { label: "Prayer", href: "#prayer" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.nav
        data-testid="main-navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 30 }}
        className={`fixed top-0 left-0 right-0 z-50 w-full px-6 md:px-12 py-3.5 flex items-center justify-between transition-colors duration-300 ${
          scrolled
            ? "bg-white shadow-[0_2px_20px_rgb(0,0,0,0.08)]"
            : "bg-white/95 backdrop-blur-sm"
        }`}
      >
        {/* Logo */}
        <button
          onClick={() => scrollTo("#home")}
          className="flex items-center gap-3 group"
          data-testid="navbar-logo"
        >
          <img src={LOGO_URL} alt="Winners Chapel Logo" className="h-12 w-12 object-contain rounded-xl bg-white" />
          <span className="hidden sm:block font-bold text-stone-900 text-base leading-tight">
            Winners Chapel
            <br />
            <span className="text-sm font-semibold text-red-600">International Ipswich</span>
          </span>
        </button>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="px-5 py-2.5 text-sm font-semibold text-stone-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => scrollTo("#giving")}
            data-testid="nav-giving-btn"
            className="ml-2 px-5 py-2.5 text-sm font-semibold bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors duration-200 shadow-sm"
          >
            Give
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2.5 rounded-lg hover:bg-red-50 transition-colors"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-[72px] left-0 right-0 z-40 bg-white shadow-lg border-t border-stone-100 px-6 py-4 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollTo(link.href)}
                  className="text-left px-4 py-3 rounded-lg text-stone-700 font-medium hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => { setMobileOpen(false); scrollTo("#giving"); }}
                className="text-left px-4 py-3 rounded-lg bg-red-600 text-white font-semibold text-center mt-1"
              >
                Give
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
